import {
  Context,
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { ICityLiteAsset } from "models/Assets/ICityAsset";
import {
  ICategoryLiteAsset,
  ICategoryLiteWithChildsAsset,
} from "models/Assets/ICategoryAsset";
import { INeighborhoodLiteAsset } from "models/Assets/INeighborhoodAsset";
import {
  ICpOption,
  IDynamicRangeAttributes,
} from "models/Assets/IDynamicAttributes";
import { ILayoutProps } from "models/base/ILayoutProps";
import { useTheme } from "styled-components";
import {
  ICountryAsset,
  ICountryAssetLite,
  ICurrency,
} from "models/Assets/ICountryAsset";
import { filterCountryByCountryCode } from "dataLayer/filters/filterCountryByCountryCode";
import { __getAjaxClient } from "utils/misc/fetchClient";
import { qsStringify } from "helpers/globalHelpers";
import { parseRouteObjectByIds } from "dataLayer/filters/parseRouteObjectByIds";
import { cleanGtmExtraData, setGtmExtraData } from "GTMs/GtmData";
import { useRouter } from "next/router";
import { useDoWhenAssetsLoaded } from "contexts/AssetsContext";
import {CATEGORY_CHANGE_EVENT_NAME} from "../hooks/useGetCategory";
import {SUBCATEGORY_CHANGE_EVENT_NAME} from "../hooks/useGetSubCategory";
import {CITY_CHANGE_EVENT_NAME} from "../hooks/useGetCity";

export type DynamicRangeAttributeQueryString = {
  [fieldName: string]: {
    from?: number;
    to?: number;
    unit?: number;
  };
};

export type SelectableCPsQueryString = {
  [fieldName: string]: (number|string)[];
};

export interface IRouteObjectsIds {
  currencyId?: number;
  cityId?: number;
  neighborhoodIds?: number[];
  categoryId?: number;
  subCategoryId?: number;
  dynamicAttributes?:
    | SelectableCPsQueryString
    | DynamicRangeAttributeQueryString;
}

export interface IRouteObjects {
  stamp?: number;
  country?: ICountryAsset | ICountryAssetLite;
  currency?: ICurrency;
  city?: ICityLiteAsset;
  neighborhoods?: INeighborhoodLiteAsset[];
  category?: ICategoryLiteAsset;
  subCategory?: ICategoryLiteAsset;
  selectedCpsOptions?: ICpOption[] | null;
  dynamicRangeAttributes?: IDynamicRangeAttributes;
}

export interface IRouteObjectsSetters {
  setCategory: Dispatch<SetStateAction<ICategoryLiteWithChildsAsset>>;
  setCity: Dispatch<SetStateAction<ICityLiteAsset>>;
  setCurrency: Dispatch<SetStateAction<ICurrency>>;
  setSubCategory?: Dispatch<SetStateAction<ICategoryLiteWithChildsAsset>>; //////
  setNeighborhoods?: Dispatch<SetStateAction<INeighborhoodLiteAsset[]>>; //////
  setSelectedCpsOptions?: Dispatch<SetStateAction<ICpOption[]>>; //////
}

export interface IRoutesObjectContext {
  routeObjectsIds?: IRouteObjectsIds;
  pageObjectsIds?: IRouteObjectsIds;
  routeObjects?: IRouteObjects;
  refreshAssetsByIds?: (routeObjectsIds: IRouteObjectsIds) => Promise<void>;
  routeObjectsSetters?: IRouteObjectsSetters;
}

export function useGetRoutesObjects(): IRouteObjects {
  return useContext(RoutesObjectContext).routeObjects;
}

export function useGetRoutesObjectsIds(): IRouteObjectsIds {
  return useContext(RoutesObjectContext).routeObjectsIds || {};
}

export function useSetRoutesObjects(): IRouteObjectsSetters {
  return useContext(RoutesObjectContext).routeObjectsSetters;
}

export function useRefreshAssetsByIds(): (
  routeObjectsIds: IRouteObjectsIds
) => Promise<void> {
  return useContext(RoutesObjectContext).refreshAssetsByIds;
}

const RoutesObjectContext: Context<IRoutesObjectContext> = createContext({});
const RoutesObjectContextProvider: FC<ILayoutProps> = ({
  children,
  routeObjectsIds,
  pageObjectsIds,
  routeObjects,
  categories,
  subCategories,
  countries,
  cities,
  neighborhoodsAssets,
  dynamicAttributesEnhanced,
}) => {
  const router = useRouter();
  const [stamp, setStamp] = useState<number>(0);
  const { country: countryCode, locale } = useTheme();
  const country =
    routeObjects?.country || filterCountryByCountryCode(countries, countryCode);
  const {
    city: city__default,
    neighborhoods: neighborhoods__default,
    category: category__default,
    subCategory: subCategory__default,
    selectedCpsOptions: selectedCpsOptions__default,
    dynamicRangeAttributes: dynamicRangeAttributes__default,
    currency: currency__default,
  } = parseRouteObjectByIds(
    country,
    categories,
    subCategories,
    cities,
    neighborhoodsAssets,
    dynamicAttributesEnhanced,
    routeObjects,
    { ...(pageObjectsIds || {}), ...(routeObjectsIds || {}) }
  );

  const [city, setCity] = useState<ICityLiteAsset>(city__default);
  const [neighborhoods, setNeighborhoods] = useState<INeighborhoodLiteAsset[]>(
    neighborhoods__default
  );
  const [category, setCategory] =
    useState<ICategoryLiteWithChildsAsset>(category__default);
  const [subCategory, setSubCategory] =
    useState<ICategoryLiteAsset>(subCategory__default);
  const [currency, setCurrency] = useState<ICurrency>(currency__default);
  const [dynamicRangeAttributes, setDynamicRangeAttributes] =
    useState<IDynamicRangeAttributes>(dynamicRangeAttributes__default);
  const [selectedCpsOptions, setSelectedCpsOptions] = useState<ICpOption[]>(
    selectedCpsOptions__default
  );

  useEffect(() => {
    setGtmExtraData({
      selectedCpsOptions,
      category,
      subCategory,
      city,
      neighborhoods,
    });
    setStamp(new Date().getTime());

    return () => cleanGtmExtraData();
  }, [
    city?.id,
    category?.id,
    subCategory?.id,
    currency?.id,
    JSON.stringify(selectedCpsOptions),
    JSON.stringify(neighborhoods),
  ]);


  routeObjects = {
    country,
    currency,
    city,
    neighborhoods,
    category,
    subCategory,
    selectedCpsOptions,
    dynamicRangeAttributes,
    stamp,
    ...routeObjects,
  };
  const routeObjectsSetters: IRouteObjectsSetters = {
    setCategory,
    setCity,
    setCurrency,
    setSubCategory,
    setNeighborhoods,
    setSelectedCpsOptions,
  };
  const refreshAssetsByIds = async (routeObjectsIds: IRouteObjectsIds) => {
    const qsParams = qsStringify({
      currencyId: routeObjectsIds.currencyId,
      cityId: routeObjectsIds.cityId,
      categoryId: routeObjectsIds.categoryId,
      subCategoryId: routeObjectsIds.subCategoryId,
      neighborhoodIds: routeObjectsIds.neighborhoodIds,
      dynamicAttributes: routeObjectsIds.dynamicAttributes,
      locale,
    });
      __getAjaxClient<IRouteObjects>(`/refreshAssetsByIds${qsParams}`).then(
      ({
        category,
        subCategory,
        city,
        neighborhoods,
        dynamicRangeAttributes,
        currency,
      }) => {

    if (category) {
      document.dispatchEvent(
        new CustomEvent(CATEGORY_CHANGE_EVENT_NAME, {
          detail: {
            categoryUpdated: category,
          },
        })
      );
    }

    if (subCategory) {
      document.dispatchEvent(
        new CustomEvent(SUBCATEGORY_CHANGE_EVENT_NAME, {
          detail: { subCategoryUpdated: subCategory },
        })
      );
    }

    if (city) {
      document.dispatchEvent(
        new CustomEvent(CITY_CHANGE_EVENT_NAME, {
          detail: { cityUpdated: city },
        })
      );
    }
        category && setCategory(category);
        subCategory && setSubCategory(subCategory);
        city && setCity(city);
        neighborhoods && setNeighborhoods([...neighborhoods]);
        dynamicRangeAttributes &&
          setDynamicRangeAttributes(dynamicRangeAttributes);
        currency && setCurrency(currency);

      }
    );

  };

  useEffect(() => {
    const handleRouteChange = () => {
      if (
        router.pathname === "/listing" ||
        router.pathname === "/dynamic-routes"
      ) {
        setCategory(null);
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, []);

  useDoWhenAssetsLoaded(() => {
    refreshAssetsByIds(routeObjectsIds);
  });

  return (
    <RoutesObjectContext.Provider
      value={{
        pageObjectsIds,
        routeObjectsIds,
        routeObjects,
        refreshAssetsByIds,
        routeObjectsSetters,
      }}
    >
      {children}
    </RoutesObjectContext.Provider>
  );
};
export default RoutesObjectContextProvider;
